import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Loader2, ChevronDown, ChevronRight, Crown, User, Trash2, KeyRound, Edit3 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { unidadesApi, operacionesApi } from '../../../lib/api';

const empty = { nombre: '', apellido: '', dni: '', fecha_nacimiento: '', email: '', telefono: '', activo: true };
const emptyFromProp = { propietario_id: '', dni: '', fecha_nacimiento: '', telefono: '' };

export default function OperacionesUnidades({ edificioId }) {
  const [unidades, setUnidades] = useState([]);
  const [users, setUsers] = useState([]); // todos los usuarios_edificio del edificio
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // unidad_id -> bool
  const [dialog, setDialog] = useState(null); // { mode: 'master'|'autorizado'|'edit', unidad, target? }
  const [masterMode, setMasterMode] = useState('nuevo'); // 'nuevo' | 'propietario'
  const [propietariosDisp, setPropietariosDisp] = useState([]);
  const [propForm, setPropForm] = useState(emptyFromProp);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [u, us] = await Promise.all([
        unidadesApi.list({ edificio_id: edificioId }),
        operacionesApi.listUsuariosEdificio({ edificio_id: edificioId }),
      ]);
      setUnidades((u.data || []).filter(x => x.edificio_id === edificioId));
      setUsers(us.data || []);
    } catch (e) {
      toast.error('Error al cargar');
    } finally { setLoading(false); }
  }, [edificioId]);

  useEffect(() => { reload(); }, [reload]);

  const usersByUnidad = users.reduce((acc, u) => {
    (acc[u.unidad_id] = acc[u.unidad_id] || []).push(u);
    return acc;
  }, {});

  const toggle = (uid) => setExpanded(prev => ({ ...prev, [uid]: !prev[uid] }));

  const openNew = (unidad, mode) => {
    setDialog({ mode, unidad });
    setForm(empty);
    setPropForm(emptyFromProp);
    setMasterMode('nuevo');
    if (mode === 'master') {
      operacionesApi.listPropietariosDisponibles(edificioId)
        .then(r => setPropietariosDisp(r.data || []))
        .catch(() => setPropietariosDisp([]));
    }
  };
  const openEdit = (unidad, target) => {
    setDialog({ mode: 'edit', unidad, target });
    setForm({
      nombre: target.nombre || '',
      apellido: target.apellido || '',
      dni: target.dni || '',
      fecha_nacimiento: target.fecha_nacimiento || '',
      email: target.email || '',
      telefono: target.telefono || '',
      activo: target.activo ?? true,
    });
  };
  const closeDialog = () => { setDialog(null); setForm(empty); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let res;
      if (dialog.mode === 'master') {
        if (masterMode === 'propietario') {
          if (!propForm.propietario_id) { toast.error('Elegí un propietario'); setSaving(false); return; }
          res = await operacionesApi.crearMasterFromPropietario(dialog.unidad.unidad_id, {
            propietario_id: propForm.propietario_id,
            dni: propForm.dni || null,
            fecha_nacimiento: propForm.fecha_nacimiento || null,
            telefono: propForm.telefono || null,
          });
        } else {
          res = await operacionesApi.crearMaster(dialog.unidad.unidad_id, form);
        }
      } else if (dialog.mode === 'autorizado') {
        res = await operacionesApi.crearAutorizado(dialog.unidad.unidad_id, form);
      } else if (dialog.mode === 'edit') {
        res = await operacionesApi.actualizarUsuario(dialog.target.usuario_edif_id, form);
      }
      const pw = res?.data?.password_temporal;
      if (pw) {
        toast.success(
          `Usuario creado. Contraseña temporal: ${pw}`,
          { duration: 30000, description: 'Copiá esta contraseña, no se mostrará de nuevo.' }
        );
      } else if (dialog.mode === 'master' && masterMode === 'propietario') {
        toast.success('Master asignado. Usa su login de propietario para acceder.', { duration: 8000 });
      } else {
        toast.success('Guardado');
      }
      closeDialog();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (target) => {
    if (!confirm(`¿Borrar a ${target.nombre} ${target.apellido}? Sus reservas futuras se cancelarán.`)) return;
    try {
      await operacionesApi.eliminarUsuario(target.usuario_edif_id);
      toast.success('Usuario eliminado');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleResetPw = async (target) => {
    if (!confirm(`¿Resetear contraseña de ${target.email}?`)) return;
    try {
      const r = await operacionesApi.resetPassword(target.usuario_edif_id);
      toast.success(`Nueva contraseña: ${r.data.password_temporal}`, { duration: 30000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>;

  return (
    <div className="space-y-2">
      <div className="text-xs text-slate-500 mb-3">
        Cargá un usuario master por unidad. El master va a poder agregar/sacar autorizados desde su panel.
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {unidades.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">Sin unidades</div>}
        {unidades.map(u => {
          const list = usersByUnidad[u.unidad_id] || [];
          const master = list.find(x => x.es_master);
          const autoriz = list.filter(x => !x.es_master);
          const open = !!expanded[u.unidad_id];
          return (
            <div key={u.unidad_id} className="border-b border-slate-100 last:border-b-0">
              <div className="flex items-center justify-between px-3 py-2 hover:bg-slate-50/40">
                <button onClick={() => toggle(u.unidad_id)} className="flex items-center gap-2 flex-1 text-left">
                  {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                  <span className="font-medium text-slate-900">{u.nombre_unidad}</span>
                  <span className="text-xs text-slate-500">
                    {master ? `Master: ${master.nombre} ${master.apellido}` : <span className="text-amber-600">Sin master</span>}
                    {autoriz.length > 0 && ` · ${autoriz.length} autorizados`}
                  </span>
                </button>
                {!master && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openNew(u, 'master')} data-testid={`btn-master-${u.unidad_id}`}>
                    <Crown className="w-3 h-3 mr-1" /> Cargar master
                  </Button>
                )}
              </div>
              {open && (
                <div className="bg-slate-50/50 px-6 py-2 space-y-1.5">
                  {master && (
                    <UserRow u={master} onEdit={() => openEdit(u, master)} onResetPw={() => handleResetPw(master)} canDelete={false} />
                  )}
                  {autoriz.map(a => (
                    <UserRow key={a.usuario_edif_id} u={a}
                      onEdit={() => openEdit(u, a)}
                      onDelete={() => handleDelete(a)}
                      onResetPw={() => handleResetPw(a)} canDelete />
                  ))}
                  {master && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-700" onClick={() => openNew(u, 'autorizado')}>
                      <Plus className="w-3 h-3 mr-1" /> Agregar autorizado
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-lg">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {dialog?.mode === 'master' && `Cargar master · ${dialog.unidad?.nombre_unidad}`}
                {dialog?.mode === 'autorizado' && `Agregar autorizado · ${dialog.unidad?.nombre_unidad}`}
                {dialog?.mode === 'edit' && `Editar · ${dialog.target?.nombre} ${dialog.target?.apellido}`}
              </DialogTitle>
            </DialogHeader>

            {dialog?.mode === 'master' && (
              <div className="grid grid-cols-2 gap-2 mb-3 mt-2">
                <button type="button"
                  onClick={() => setMasterMode('nuevo')}
                  className={`p-2 rounded border text-xs text-center ${masterMode === 'nuevo' ? 'border-amber-400 bg-amber-50 text-amber-900 font-medium' : 'border-slate-200 text-slate-600'}`}
                  data-testid="master-mode-nuevo">
                  Persona nueva
                </button>
                <button type="button"
                  onClick={() => setMasterMode('propietario')}
                  disabled={propietariosDisp.length === 0}
                  className={`p-2 rounded border text-xs text-center disabled:opacity-50 disabled:cursor-not-allowed ${masterMode === 'propietario' ? 'border-amber-400 bg-amber-50 text-amber-900 font-medium' : 'border-slate-200 text-slate-600'}`}
                  data-testid="master-mode-prop"
                  title={propietariosDisp.length === 0 ? 'No hay propietarios disponibles' : ''}>
                  Propietario existente {propietariosDisp.length > 0 && `(${propietariosDisp.length})`}
                </button>
              </div>
            )}

            {dialog?.mode === 'master' && masterMode === 'propietario' ? (
              <div className="space-y-3 py-2">
                <div>
                  <Label>Propietario *</Label>
                  <select className="w-full border border-slate-200 rounded h-9 px-2"
                    value={propForm.propietario_id}
                    onChange={(e) => {
                      const p = propietariosDisp.find(x => x.propietario_id === e.target.value);
                      setPropForm({
                        propietario_id: e.target.value,
                        dni: p?.dni || '',
                        fecha_nacimiento: p?.fecha_nacimiento || '',
                        telefono: p?.telefono || '',
                      });
                    }}
                    data-testid="form-prop-select"
                    required>
                    <option value="">— Elegir propietario —</option>
                    {propietariosDisp.map(p => (
                      <option key={p.propietario_id} value={p.propietario_id}>
                        {p.nombre_razon_social} ({p.email})
                      </option>
                    ))}
                  </select>
                </div>
                {propForm.propietario_id && (
                  <>
                    <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs text-slate-600">
                      <b>Login:</b> usa su email/contraseña de propietario.
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>DNI</Label><Input value={propForm.dni} onChange={(e) => setPropForm({ ...propForm, dni: e.target.value })} /></div>
                      <div><Label>Fecha nacimiento</Label><Input type="date" value={propForm.fecha_nacimiento} onChange={(e) => setPropForm({ ...propForm, fecha_nacimiento: e.target.value })} /></div>
                      <div className="col-span-2"><Label>Teléfono</Label><Input value={propForm.telefono} onChange={(e) => setPropForm({ ...propForm, telefono: e.target.value })} /></div>
                    </div>
                    <p className="text-[11px] text-slate-500">Estos datos se guardan en la ficha del propietario.</p>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 py-2">
                <div><Label>Nombre *</Label><Input required value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} data-testid="form-nombre" /></div>
                <div><Label>Apellido *</Label><Input required value={form.apellido} onChange={(e) => setForm({...form, apellido: e.target.value})} data-testid="form-apellido" /></div>
                <div><Label>DNI *</Label><Input required value={form.dni} onChange={(e) => setForm({...form, dni: e.target.value})} data-testid="form-dni" /></div>
                <div><Label>Fecha nacimiento</Label><Input type="date" value={form.fecha_nacimiento || ''} onChange={(e) => setForm({...form, fecha_nacimiento: e.target.value})} /></div>
                <div className="col-span-2"><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="form-email" /></div>
                <div className="col-span-2"><Label>Teléfono</Label><Input value={form.telefono || ''} onChange={(e) => setForm({...form, telefono: e.target.value})} /></div>
                {dialog?.mode === 'edit' && (
                  <div className="col-span-2 flex items-center gap-2">
                    <input type="checkbox" id="activo" checked={form.activo} onChange={(e) => setForm({...form, activo: e.target.checked})} />
                    <Label htmlFor="activo">Activo</Label>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="form-submit">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const UserRow = ({ u, onEdit, onDelete, onResetPw, canDelete }) => (
  <div className="flex items-center justify-between text-xs py-1">
    <div className="flex items-center gap-2">
      {u.es_master ? <Crown className="w-3 h-3 text-amber-600" /> : <User className="w-3 h-3 text-slate-400" />}
      <span className="font-medium text-slate-800">{u.nombre} {u.apellido}</span>
      <span className="text-slate-500">DNI {u.dni}</span>
      <span className="text-slate-400">·</span>
      <span className="text-slate-500">{u.email}</span>
      {!u.activo && <span className="text-red-500 ml-1">(inactivo)</span>}
    </div>
    <div className="flex items-center gap-1">
      <button onClick={onEdit} className="p-1 hover:bg-slate-200 rounded" title="Editar"><Edit3 className="w-3 h-3 text-slate-500" /></button>
      <button onClick={onResetPw} className="p-1 hover:bg-slate-200 rounded" title="Reset password"><KeyRound className="w-3 h-3 text-slate-500" /></button>
      {canDelete && <button onClick={onDelete} className="p-1 hover:bg-red-100 rounded" title="Eliminar"><Trash2 className="w-3 h-3 text-red-500" /></button>}
    </div>
  </div>
);
